import * as React from 'react';
import {
  ArrowDownload20Regular,
  ArrowUpload20Regular,
  CheckmarkCircle20Filled,
  DiamondDismiss20Filled,
  Dismiss20Regular,
  Info20Filled,
  Warning20Filled,
} from '@fluentui/react-icons';
import { cn } from 'cn';
import { toast as sonner } from 'sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@web/shared/ui/avatar';
import { Link } from '@web/shared/ui/link';
import { Spinner } from '@web/shared/ui/spinner';

/**
 * Toast — a notificação passageira. Diz que algo aconteceu — "Contato salvo", "Arquivo enviado" — e some sozinha.
 * O que exige resposta vai no `Dialog`; erro de campo vai no campo.
 *
 * Anatomia (320 de largura para o título caber numa linha), superfície branca, raio 4, sombra 16, 12 de padding:
 *
 *   cabeçalho  ícone de 20 · título 14/20 semibold · "end container": o botão de fechar, OU o
 *              horário (12/16), OU a ação (link 14) — um de cada vez
 *   corpo      recuado 28 (o ícone mais o vão), com a informação secundária 14/20, a terciária
 *              12/16 em `--fg-secondary`, a barra de progresso (2px, com "N% concluído") e a
 *              linha de ações rápidas (links, 16 de vão)
 *
 * Os STATUS são os da especificação: `info`, `success`, `warning`, `error` (as cores de situação,
 * `--status-*`), `upload`, `download`, `loading` (o `Spinner` extra-tiny) — ou um `icon`
 * próprio, ou um `avatar` (16px). A cor vai só no ícone; o texto é `--foreground`.
 *
 * `Toast` é o DESENHO. Quem dispara é a função `toast` abaixo, por cima do sonner, e quem
 * posiciona é o `Toaster` (`sonner.tsx`): inferior, centralizado. Todo texto — título, corpo,
 * ações, o rótulo do botão de fechar — vem da tela.
 */

export type ToastStatus =
  'info' | 'success' | 'warning' | 'error' | 'loading' | 'upload' | 'download';

export interface ToastAction {
  label: React.ReactNode;
  onClick: () => void;
}

export interface ToastProps {
  title: React.ReactNode;
  status?: ToastStatus;
  /** Um ícone de 20 no lugar do ícone de status. */
  icon?: React.ReactNode;
  /** A pessoa de quem a notícia vem, em vez do ícone. */
  avatar?: { src?: string; initials: string };
  /** Informação secundária (14/20). */
  body?: React.ReactNode;
  /** Informação terciária (12/16, apagada). */
  detail?: React.ReactNode;
  /** A barra de progresso: `value` de 0 a 100 e o texto embaixo ("40% concluído"). */
  progress?: { value: number; label: React.ReactNode };
  /** Ações rápidas, no rodapé do corpo. */
  actions?: ToastAction[];
  /** A ação do cabeçalho — ocupa o lugar do botão de fechar. */
  action?: ToastAction;
  /** O horário, no cabeçalho — ocupa o lugar do botão de fechar. */
  timestamp?: React.ReactNode;
  /** Fecha o toast. Sem `action` nem `timestamp`, vira o botão × do cabeçalho. */
  onDismiss?: () => void;
  /** O rótulo do botão ×. Obrigatório quando ele aparece; o `Toaster` entrega o seu. */
  dismissLabel?: string;
  className?: string;
}

const STATUS_ICON: Record<
  Exclude<ToastStatus, 'loading'>,
  { icon: React.ReactNode; color: string }
> = {
  info: { icon: <Info20Filled />, color: 'text-fg-secondary' },
  success: { icon: <CheckmarkCircle20Filled />, color: 'text-status-success' },
  warning: { icon: <Warning20Filled />, color: 'text-status-warning' },
  error: { icon: <DiamondDismiss20Filled />, color: 'text-status-error' },
  upload: { icon: <ArrowUpload20Regular />, color: 'text-fg-secondary' },
  download: { icon: <ArrowDownload20Regular />, color: 'text-fg-secondary' },
};

/** O rótulo do × e o que mais o `Toaster` decide pela aplicação inteira. */
export const ToastConfigContext = React.createContext<{ dismissLabel: string } | null>(null);

function ToastGlyph({ status, icon, avatar }: Pick<ToastProps, 'status' | 'icon' | 'avatar'>) {
  if (avatar) {
    return (
      <Avatar size="sm" className="size-4">
        {avatar.src && <AvatarImage src={avatar.src} alt="" />}
        <AvatarFallback className="bg-brand-tint text-brand-fg text-[8px] font-semibold">
          {avatar.initials}
        </AvatarFallback>
      </Avatar>
    );
  }
  if (icon) return <span className="text-fg-secondary [&_svg]:size-5">{icon}</span>;
  if (status === 'loading') return <Spinner size="extra-tiny" aria-label="" aria-hidden />;
  const { icon: glyph, color } = STATUS_ICON[status ?? 'info'];
  return <span className={cn('[&_svg]:size-5', color)}>{glyph}</span>;
}

function Toast({
  title,
  status,
  icon,
  avatar,
  body,
  detail,
  progress,
  actions,
  action,
  timestamp,
  onDismiss,
  dismissLabel,
  className,
}: ToastProps) {
  const config = React.useContext(ToastConfigContext);
  const closeLabel = dismissLabel ?? config?.dismissLabel;
  const hasBody = body !== undefined || detail !== undefined || progress || actions?.length;

  return (
    <div
      role="status"
      data-slot="toast"
      data-status={status}
      className={cn(
        'bg-surface text-foreground shadow-fluent-16 flex w-[320px] min-w-[320px] flex-col rounded-[4px] p-3',
        className,
      )}
    >
      <div data-slot="toast-header" className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <span aria-hidden className="flex size-5 shrink-0 items-center justify-center">
            <ToastGlyph status={status} icon={icon} avatar={avatar} />
          </span>
          <p className="type-body1-strong min-w-0 flex-1 break-words">{title}</p>
        </div>
        <div className="flex h-5 shrink-0 items-center">
          {action ? (
            <Link asChild variant="default" className="type-body1 text-link">
              <button type="button" onClick={action.onClick}>
                {action.label}
              </button>
            </Link>
          ) : timestamp !== undefined ? (
            <span className="type-caption1 whitespace-nowrap">{timestamp}</span>
          ) : onDismiss ? (
            <button
              type="button"
              aria-label={closeLabel}
              onClick={onDismiss}
              className="text-fg-secondary hover:text-foreground flex size-5 items-center justify-center rounded-[2px] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_svg]:size-5"
            >
              <Dismiss20Regular />
            </button>
          ) : null}
        </div>
      </div>

      {hasBody && (
        <div data-slot="toast-body" className="flex flex-col pl-7">
          {body !== undefined && <p className="type-body1 break-words">{body}</p>}
          {detail !== undefined && (
            <p className="type-caption1 text-fg-secondary break-words">{detail}</p>
          )}
          {progress && (
            <div className="flex flex-col gap-2 py-2">
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress.value)}
                className="bg-progress-track h-[2px] w-full overflow-hidden rounded-full"
              >
                <div
                  className="bg-brand h-full rounded-full transition-[width] duration-(--duration-normal)"
                  style={{ width: `${Math.min(100, Math.max(0, progress.value))}%` }}
                />
              </div>
              <p className="type-caption1">{progress.label}</p>
            </div>
          )}
          {actions && actions.length > 0 && (
            <div className="flex flex-wrap gap-4 pt-[7px]">
              {actions.map((quick, index) => (
                <Link key={index} asChild variant="default" className="type-body1 text-link">
                  <button type="button" onClick={quick.onClick}>
                    {quick.label}
                  </button>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ disparo */

/** O tempo: 3s para o que é só aviso; o que está em andamento fica até acabar. */
const DEFAULT_DURATION = 3000;

export type ToastOptions = Omit<ToastProps, 'onDismiss' | 'className'> & {
  /** Reaproveitar um id ATUALIZA o toast no lugar (o upload que vira "concluído"). */
  id?: string | number;
  /** Em ms. `Infinity` deixa aberto até fechar à mão ou por `toast.dismiss`. */
  duration?: number;
  /** `false` tira o × — o toast só sai sozinho ou por `toast.dismiss`. */
  dismissible?: boolean;
};

function show(options: ToastOptions): string | number {
  const { id, duration, dismissible = true, ...props } = options;
  const persistent = props.status === 'loading' || props.progress !== undefined;
  return sonner.custom(
    (toastId) => (
      <Toast {...props} onDismiss={dismissible ? () => sonner.dismiss(toastId) : undefined} />
    ),
    { id, duration: duration ?? (persistent ? Infinity : DEFAULT_DURATION) },
  );
}

const withStatus =
  (status: ToastStatus) =>
  (options: Omit<ToastOptions, 'status'>): string | number =>
    show({ ...options, status });

/**
 * `toast.success({ title })`, `toast.error({ title, body })`, `toast.loading({ title })`…
 * Todas devolvem o id; `toast.update(id, { … })` troca o conteúdo no lugar e
 * `toast.dismiss(id)` fecha (sem id, fecha todos).
 */
export const toast = {
  show,
  info: withStatus('info'),
  success: withStatus('success'),
  warning: withStatus('warning'),
  error: withStatus('error'),
  loading: withStatus('loading'),
  upload: withStatus('upload'),
  download: withStatus('download'),
  update: (id: string | number, options: Omit<ToastOptions, 'id'>) => show({ ...options, id }),
  dismiss: (id?: string | number) => sonner.dismiss(id),
};

export { Toast };
